import { Component, Input, OnInit, AfterViewInit, OnDestroy, OnChanges, ViewChild, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef, SimpleChanges } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-common-map',
    templateUrl: './common-map.component.html',
    styleUrls: ['./common-map.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommonMapComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {

    @ViewChild('mapContainer', { static: false }) mapContainer?: ElementRef;

    // Inputs
    @Input() kmlUrls: string[] = [];
    @Input() mapCenter: google.maps.LatLngLiteral = { lat: 20.5937, lng: 78.9629 }; // Default: India
    @Input() mapZoom: number = 15;
    @Input() mapTitle: string = 'Map';
    @Input() showFullscreenButton: boolean = true;

    // Map instance
    map: google.maps.Map | null = null;
    private kmlLayers: google.maps.KmlLayer[] = [];
    isFullScreen: boolean = false;
    private destroy$ = new Subject<void>();

    // Map options
    mapOptions: google.maps.MapOptions = {
        zoomControl: true,
        scrollwheel: true,
        draggableCursor: 'pointer',
        mapTypeId: google.maps.MapTypeId.SATELLITE,
        fullscreenControl: false,
        streetViewControl: false
    };

    constructor(private cdr: ChangeDetectorRef) { }

    ngOnInit(): void {
        // Component initialized
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Handle input property changes
        if (changes['kmlUrls'] && !changes['kmlUrls'].firstChange && this.map) {
            // kmlUrls changed after initial initialization, reload KML layers
            this.clearKmlLayers();
            if (this.kmlUrls && this.kmlUrls.length > 0) {
                this.loadKmlLayers();
                this.cdr.markForCheck();
            }
        }
    }

    ngAfterViewInit(): void {
        // Initialize the map after view is initialized
        // Wait for container to have proper dimensions before initializing
        this.waitForMapDimensions('commonMapContainer');
    }

    /**
     * Wait for map container to have proper dimensions before initializing
     */
    private waitForMapDimensions(containerId: string, attempts: number = 0): void {
        const mapElement = document.getElementById(containerId);
        if (!mapElement) {
            console.warn(`Container element not found: ${containerId}`);
            return;
        }

        const rect = mapElement.getBoundingClientRect();
        // If container has zero dimensions, retry
        if ((rect.width === 0 || rect.height === 0) && attempts < 15) {
            setTimeout(() => {
                this.waitForMapDimensions(containerId, attempts + 1);
            }, 150);
            return;
        }

        // If still zero after all attempts, log and try anyway
        this.initializeMap(containerId);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.clearKmlLayers();
    }

    /**
     * Initialize map in the specified container
     */
    private initializeMap(containerId: string): void {
        const mapElement = document.getElementById(containerId);
        if (!mapElement) {
            console.warn(`Map element with id "${containerId}" not found`);
            return;
        }

        // Get element dimensions
        const rect = mapElement.getBoundingClientRect();

        // Check if element has proper dimensions
        if (rect.width === 0 || rect.height === 0) {
            console.warn(`Map element has zero dimensions, retrying...`);
            setTimeout(() => {
                this.initializeMap(containerId);
            }, 300);
            return;
        }

        // Determine if on mobile
        const isMobile = window.innerWidth <= 768;

        const mapProperties: google.maps.MapOptions = {
            center: this.mapCenter,
            zoom: this.mapZoom,
            mapTypeId: google.maps.MapTypeId.SATELLITE,
            zoomControl: true,
            scrollwheel: !isMobile, // Disable scrollwheel on mobile to allow page scrolling
            draggableCursor: 'pointer',
            fullscreenControl: false,
            streetViewControl: false,
            gestureHandling: 'cooperative' // Better touch handling on mobile
        };

        try {
            this.map = new google.maps.Map(mapElement, mapProperties);

            // Load KML layers after map is initialized
            if (this.kmlUrls && this.kmlUrls.length > 0) {
                this.loadKmlLayers();
            }
        } catch (error) {
            console.error('Error initializing map:', error);
        }
    }

    /**
     * Load KML files onto the map
     */
    private loadKmlLayers(): void {
        this.clearKmlLayers();

        this.kmlUrls.forEach(url => {
            if (url && url.trim() && this.map) {
                const kmlLayer = new google.maps.KmlLayer({
                    url: url,
                    map: this.map,
                    suppressInfoWindows: false
                });

                kmlLayer.addListener('status_changed', () => {
                    if (kmlLayer.getStatus() === google.maps.KmlLayerStatus.OK) {
                        // KML loaded successfully
                        console.log('KML Layer loaded:', url);
                    }
                });

                this.kmlLayers.push(kmlLayer);
            }
        });
    }

    /**
     * Clear all KML layers
     */
    private clearKmlLayers(): void {
        this.kmlLayers.forEach(layer => {
            layer.setMap(null);
        });
        this.kmlLayers = [];
    }

    /**
     * Toggle fullscreen view
     */
    toggleFullScreen(): void {
        this.isFullScreen = !this.isFullScreen;
        this.cdr.markForCheck();

        if (this.isFullScreen) {
            // Initialize map in fullscreen container
            setTimeout(() => {
                this.initializeMap('commonMapFullscreenContainer');
            }, 300);
        } else {
            // Reinitialize map back to normal container
            setTimeout(() => {
                this.initializeMap('commonMapContainer');
            }, 300);
        }
    }

    /**
     * Exit fullscreen
     */
    exitFullScreen(): void {
        this.isFullScreen = false;
        this.cdr.markForCheck();

        // Reinitialize map back to normal container
        setTimeout(() => {
            this.initializeMap('commonMapContainer');
        }, 300);
    }
}
